import { createConnectTransport } from "@connectrpc/connect-web";
import { maintenanceStore } from "@/shared/stores/MaintenanceStore";
import { setServerTimeDelta } from "@/shared/utils/serverTime";
import { getDeviceFingerprint } from "@/shared/utils/fingerprint";

const API_BASE_URL = import.meta.env.VITE_API_URL || "";

export const rpcTransport = createConnectTransport({
  baseUrl: `${API_BASE_URL}/rpc`,
  // TODO: switch to useBinaryFormat: true after debugging

  fetch: (async (input: RequestInfo | URL, init?: RequestInit) => {
    // Use the app's selected locale (stored in localStorage) so the backend resolves
    // locale-specific names (e.g. payment method nameTranslations) in the app language,
    // not the browser's OS language which may differ.
    const appLocale = localStorage.getItem("locale");
    const fingerprint = await getDeviceFingerprint();
    const requestStartedAt = performance.now();
    const response = await globalThis.fetch(input, {
      ...init,
      credentials: "include",
      headers: {
        ...Object.fromEntries(new Headers(init?.headers).entries()),
        "X-Internal-App": "client",
        ...(appLocale ? { "Accept-Language": appLocale } : {}),
        ...(fingerprint ? { "X-Device-Fingerprint": fingerprint } : {}),
      },
    });
    if (response.status === 503) {
      maintenanceStore.setEnabled(true);
    }
    const serverTime = response.headers.get("X-Server-Time");
    if (serverTime) {
      const serverResponseTimestamp = Number(serverTime);
      const serverRequestTime = response.headers.get("X-Server-Request-Time");
      const serverRequestTimestamp = serverRequestTime === null ? null : Number(serverRequestTime);
      const roundTripMs = performance.now() - requestStartedAt;
      const hasServerTiming = serverRequestTimestamp !== null && Number.isFinite(serverRequestTimestamp);
      const serverProcessingMs = hasServerTiming
        ? Math.max(0, serverResponseTimestamp - serverRequestTimestamp)
        : 0;
      const estimatedResponseTransitMs = Math.max(0, roundTripMs - serverProcessingMs) / 2;
      setServerTimeDelta(serverResponseTimestamp + estimatedResponseTransitMs, roundTripMs);
    }
    return response;
  }) as typeof globalThis.fetch,
});
